import {
  parseVidsString,
  rangesOverlap,
  rangeToVidsString,
  type VidRange,
} from "~/units/scripture";
import type { CommentaryFilters } from "~/stores/useCommentaryFiltersStore";
import { requireCommentaryType } from "~/static/taxonomy/commentaryTypes";
import type { Locale } from "vue-i18n";

/**
 * The filters of the commentary page to which we should link from this comment.
 *
 * We must make sure that these filters will include the current comment, else the user experience becomes very
 * confusing.
 *
 * @param filters The stored commentary filters.
 * @param ranges The scripture ranges associated with the current comment.
 * @param commentaryTypeMachineName The machine name of the commentary type associated with the current comment.
 * @param detailLevelWeight The weight of the detail level associated with the current comment.
 * @param fallbackVids The default VID range to fall back to.
 */
export function computeCommentaryFilters(
  filters: Partial<CommentaryFilters>,
  ranges: VidRange[],
  commentaryTypeMachineName: string,
  detailLevelWeight: number,
  fallbackVids: string,
): CommentaryFilters {
  // We would like to use the VID range from the stored commentary filters, but only if it overlaps with one of the
  // current comment's ranges.
  let vids;
  if (commentRangesOverlapWithStoredCommentaryFilter(filters, ranges)) {
    vids =
      filters.vids ||
      (ranges[0] == null ? undefined : rangeToVidsString(ranges[0])) ||
      fallbackVids;
  } else {
    vids =
      (ranges[0] == null ? undefined : rangeToVidsString(ranges[0])) ||
      filters.vids ||
      fallbackVids;
  }

  // If the stored commentary filters have a lower detail level that the current comment, we need to broaden it to
  // ensure that our comment is included.
  const weight = Math.max(
    filters.detailLevelWeight || Number.MIN_SAFE_INTEGER,
    detailLevelWeight,
  );

  return {
    // The comment will never be shown on a commentary page of a different type,
    // so we must always use the commentary type from the current comment.
    commentaryTypeMachineName,
    detailLevelWeight: weight,
    vids,
  };
}

/**
 * Check whether there is some overlap between the comment's range and the stored commentary filter's range.
 * If there is no overlap, then the commentary page stored in the filters does not contain this comment, and so
 * we should rather use the range of the current comment.
 *
 * @param filters The stored commentary filters.
 * @param ranges The scripture ranges associated with the current comment.
 */
function commentRangesOverlapWithStoredCommentaryFilter(
  filters: Partial<CommentaryFilters>,
  ranges: VidRange[],
): boolean {
  if (!filters.vids) {
    // No stored commentary filter, therefore no overlap.
    return false;
  }

  const storedRange =
    parseVidsString(
      {
        vids: filters.vids,
      }.vids,
      null,
    )[0] || null;
  if (!storedRange) {
    return false;
  }

  return ranges.findIndex((r) => rangesOverlap(r, storedRange)) > -1;
}

export function getCommentaryNameForBreadcrumb(
  filters: CommentaryFilters,
  locale: Locale,
): string {
  try {
    return (
      requireCommentaryType(filters.commentaryTypeMachineName).name[locale] ??
      ""
    );
  } catch (e) {
    return "Unknown";
  }
}
